import type { ObjectMetadata } from '@olytic/foundation';

import { formatMoney } from '../../objects/format';
import { leadName, OBJECT_DEFS } from '../../objects/objectDefs';
import { PATHS, pathOf } from '../../objects/paths';
import {
  activityColumns, asDate, asDateTime, asLabel, asCheck, assignmentColumns, column, createdByField, documentColumns, lastModifiedByField, lookup, railMeetingColumns,
  listColumnsOf, related, type Row, schemaRecord, show, text,
} from './kit';

const def = OBJECT_DEFS.opportunities;

/**
 * Its fields come from the database (the server's SCHEMA_OBJECTS), shown by
 * their type, lookups linked; any column not placed below appears in Other Fields.
 */
const field = schemaRecord('opportunities');

/** The opportunity's Path: open stages, then Closed (won or lost). See objects/paths.ts. */
export const opportunityPath = (row: Row) => pathOf(PATHS.opportunities!, row);

const details = [
  field('name', 'Opportunity Name'),
  field('account_name', 'Account'),
  field('stage', 'Stage', {
    help: 'Where the deal stands: Qualify, Shape, Propose, Negotiate, Commit, then Closed Won or Closed Lost. Closing needs a Deal Retro.',
  }),
  field('amount', 'Amount'),
  field('probability', 'Probability', { help: 'How likely the deal is to close, as a percent.' }),
  field('primary_quote_name', 'Primary Quote', { help: "The quote marked Primary. Only this opportunity's quotes can be chosen." }),
  field('close_date', 'Close Date'),
  field('next_step', 'Next Step'),
  // A person, edited like any lookup; a name, not a code.
  field('owner_name', 'Owner', { format: text, link: lookup('people', 'owner_id') }),
  field('created_at', 'Created'),
  field('updated_at', 'Last Modified'),
  field('closed', 'Closed'),
  field('won', 'Won'),
  field('lost', 'Lost'),
  createdByField(),
  lastModifiedByField(),
];

/** An opportunity: a deal in the pipeline. Rationale: opportunities.md. */
export const opportunities: ObjectMetadata<Row> = {
  key: def.key,
  singular: 'Opportunity',
  plural: def.label,
  icon: def.icon,
  listColumns: listColumnsOf(def, details),
  details,
  sections: [
    { key: 'identity', fields: ['name', 'account_name', 'stage', 'amount', 'primary_quote_name', 'probability', 'close_date', 'next_step', 'owner_name'] },
    { key: 'system', label: 'System', collapsed: true, fields: ['created_at', 'created_by_name', 'updated_at', 'last_modified_by_name', 'closed', 'won', 'lost'] },
  ],
  related: [
    // Rows are roles; the name links to the contact. Role and Primary edit in place (ObjectRecordView).
    related('contact-roles', 'Opportunity Contacts', undefined, [
      column('name', 'Name', { link: lookup('contacts', 'contact_id') }),
      column('title', 'Title'),
      column('email', 'Email'),
      { ...column('role', 'Role', { format: asLabel }), editActivation: 'click' },
      { ...column('is_primary', 'Primary'), render: (row) => asCheck(row.is_primary), editActivation: 'click' },
    ]),
    // Why the deal was won or lost; closing needs one (migration 0053).
    related('deal-retros', 'Deal Retros', 'deal-retros', [
      column('name', 'Created by', { primary: true }),
      column('notes', 'Notes'),
      column('created_at', 'Created', { format: asDateTime }),
    ]),
    related('quotes', 'Quotes', 'quotes', [
      column('quote_number', 'Quote', { primary: true }),
      column('name', 'Name'),
      column('status', 'Status', { format: asLabel }),
      column('one_time_net', 'One-Time', { format: show(formatMoney), align: 'right' }),
      column('monthly_net', 'Monthly', { format: show(formatMoney), align: 'right' }),
    ]),
    related('deliverables', 'Deliverables', 'deliverables', [
      column('name', 'Deliverable', { primary: true }),
      column('status', 'Status', { format: asLabel }),
      { ...column('is_exemplar', 'Exemplar'), render: (row) => asCheck(row.is_exemplar) },
      column('updated_at', 'Last Modified', { format: asDateTime }),
    ]),
    related('meetings', 'Meetings', 'meetings', railMeetingColumns(column('meeting_category', 'Category', { format: asLabel }))),
    related('activities', 'Activities', 'activities', activityColumns().slice(0, 3)),
    related('leads', 'Leads', 'leads', [
      { ...column('first_name', 'Name', { primary: true }), get: leadName },
      column('maturity_tier', 'Maturity', { format: asLabel }),
      column('created_at', 'Submitted', { format: asDate }),
    ]),
    related('documents', 'Documents', 'documents', [documentColumns()[0]!, documentColumns()[2]!]),
    // Period, amount and hours edit in place (the record page saves them as Edit schedules does).
    related('revenue', 'Revenue Schedule', 'revenue', [
      column('name', 'Month and Line', { primary: true }),
      { ...column('period_start', 'Start', { format: asDate }), editor: { kind: 'date' }, editActivation: 'click' },
      { ...column('period_end', 'End', { format: asDate }), editor: { kind: 'date' }, editActivation: 'click' },
      { ...column('amount', 'Amount', { format: show(formatMoney), align: 'right' }), editor: { kind: 'number', step: 0.01 }, editActivation: 'click' },
      {
        ...column('hours', 'Hours', { format: show((v) => String(v)), align: 'right' }),
        editor: { kind: 'number', min: 0, step: 0.5, nullable: true },
        editActivation: 'click',
      },
    ]),
    related('billing-schedules', 'Billing Schedule', 'billing-schedules', [
      column('name', 'Line and Billing', { primary: true }),
      column('planned_date', 'Invoice Date', { format: asDate }),
      column('amount', 'Amount', { format: show(formatMoney), align: 'right' }),
      column('status', 'Status', { format: asLabel }),
      column('source', 'On', { format: asLabel }),
    ]),
    related('resource-assignments', 'Resource Assignments', 'resource-assignments', assignmentColumns()),
  ],
  // Details, then the page's own tabs (Revenue, Contracting, Delivery, Activity:
  // ObjectRecordView), then Related with every list above, each full width.
  tabs: [{ key: 'details', label: 'Details', details: true }],
  titleOf: (row) => text(row.name),
  searchKeys: ['name', 'account_name'],
};
